import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeft, DollarSign, Calendar
} from 'lucide-react';
import api from '../api/api';
import PageShell from './ui/PageShell';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SnackbarContext } from '../App';

const STAGES = ['NEW', 'QUALIFYING', 'MEETING', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'];
const SOURCES = ['INBOUND', 'OUTBOUND', 'REFERRAL', 'PARTNER', 'WEBSITE', 'EVENT', 'OTHER'];

export default function OpportunityForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const showSnackbar = useContext(SnackbarContext);
    const isEditMode = !!id;

    const [loading, setLoading] = useState(isEditMode);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: '', stage: 'NEW', expectedValue: 0, expectedCloseDate: '',
        companyId: '', primaryContactId: '', ownerId: '', source: '', notes: '', currency: 'USD'
    });

    const [workspaceCompanies, setWorkspaceCompanies] = useState([]);
    const [workspaceContacts, setWorkspaceContacts] = useState([]);
    const [workspaceMembers, setWorkspaceMembers] = useState([]);

    useEffect(() => {
        const fetchRefs = async () => {
            try {
                const [companiesRes, contactsRes, membersRes] = await Promise.allSettled([
                    api.get('/api/crm/companies?limit=250'),
                    api.get('/api/contacts?limit=100'),
                    api.get('/api/opportunities/workspace-members')
                ]);
                if (companiesRes.status === 'fulfilled') {
                    const data = companiesRes.value.data?.data || companiesRes.value.data || [];
                    setWorkspaceCompanies(data.filter(c => c._id || c.id));
                }
                if (contactsRes.status === 'fulfilled') {
                    const data = contactsRes.value.data?.data || contactsRes.value.data || [];
                    setWorkspaceContacts(data.filter(c => c._id || c.id));
                }
                if (membersRes.status === 'fulfilled') {
                    const data = membersRes.value.data?.data || membersRes.value.data || [];
                    setWorkspaceMembers(Array.isArray(data) ? data : (data.members || []));
                }
            } catch (_) {}
        };
        fetchRefs();
    }, []);

    useEffect(() => {
        if (isEditMode) {
            const fetchOpp = async () => {
                try {
                    const res = await api.get(`/api/opportunities/${id}`);
                    const opp = res.data.opportunity;
                    setFormData({
                        title: opp.title || '',
                        stage: opp.stage || 'NEW',
                        expectedValue: opp.expectedValue || 0,
                        expectedCloseDate: opp.expectedCloseDate ? new Date(opp.expectedCloseDate).toISOString().split('T')[0] : '',
                        companyId: opp.companyId?._id || opp.companyId || '',
                        primaryContactId: opp.primaryContactId?._id || opp.primaryContactId || '',
                        ownerId: opp.ownerId?._id || opp.ownerId || '',
                        source: opp.source || '',
                        notes: opp.notes || '',
                        currency: opp.currency || 'USD'
                    });
                } catch (err) {
                    showSnackbar('Failed to load opportunity', 'error');
                    navigate('/opportunities');
                } finally {
                    setLoading(false);
                }
            };
            fetchOpp();
        }
    }, [id, isEditMode, navigate, showSnackbar]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.title) { showSnackbar('Title is required', 'error'); return; }
        
        setSubmitting(true);
        try {
            const payload = { ...formData };
            // Clean empty strings to null for optional refs
            if (!payload.companyId) payload.companyId = null;
            if (!payload.primaryContactId) payload.primaryContactId = null;
            if (!payload.ownerId) payload.ownerId = null;
            if (!payload.source) payload.source = null;

            if (isEditMode) {
                await api.put(`/api/opportunities/${id}`, payload);
                showSnackbar('Opportunity updated successfully', 'success');
                navigate(`/opportunities/${id}`);
            } else {
                const res = await api.post('/api/opportunities', payload);
                showSnackbar('Opportunity created successfully', 'success');
                navigate(`/opportunities/${res.data.opportunity._id}`);
            }
        } catch (err) {
            showSnackbar(err.isAppError ? err.message : 'Operation failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const getContactName = (contact) => {
        if (!contact) return null;
        if (contact.displayName) return contact.displayName;
        const pm = contact.personMasterId;
        if (pm) {
            if (typeof pm === 'object') return pm.firstName ? `${pm.firstName} ${pm.lastName || ''}`.trim() : pm.canonicalEmail;
            return 'Contact';
        }
        return contact.email || 'Contact';
    };

    if (loading) return <PageShell><div className="flex h-[60vh] items-center justify-center font-black uppercase tracking-widest text-muted-foreground animate-pulse">Intake in progress...</div></PageShell>;

    return (
        <PageShell>
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="flex items-center justify-between bg-card/40 backdrop-blur-sm p-8 rounded-[2.5rem] border border-border shadow-sm">
                    <div className="flex items-center gap-6">
                        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}
                            className="h-14 w-14 rounded-2xl hover:bg-muted/50 transition-all border border-transparent hover:border-border">
                            <ChevronLeft className="w-6 h-6" />
                        </Button>
                        <div className="space-y-1">
                            <h1 className="text-3xl font-black text-foreground tracking-tight">
                                {isEditMode ? 'Edit Opportunity' : 'New Revenue Stream'}
                            </h1>
                            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">
                                {isEditMode ? `Updating ${formData.title}` : 'Define a new potential deal in your pipeline'}
                            </p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* General Information */}
                    <div className="md:col-span-2 bg-card shadow-sm rounded-[2.5rem] p-8 border border-border space-y-8">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary border-b border-border/50 pb-4">Core Identification</h3>
                        
                        <div className="space-y-1.5 flex-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Deal Title *</label>
                            <input 
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="e.g. Enterprise License Expansion - Q4"
                                className="w-full bg-muted/20 border-2 border-border/50 p-4 rounded-2xl text-base font-bold focus:border-primary outline-none transition-all"
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Current Stage</label>
                                <Select value={formData.stage} onValueChange={(val) => setFormData(prev => ({ ...prev, stage: val }))}>
                                    <SelectTrigger className="w-full h-14 bg-muted/20 border-2 border-border/50 text-sm font-bold rounded-2xl px-5">
                                        <SelectValue placeholder="Select stage" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-border shadow-2xl">
                                        {STAGES.map(s => <SelectItem key={s} value={s} className="text-xs font-bold">{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5 text-black">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Acquisition Source</label>
                                <Select value={formData.source || ""} onValueChange={(val) => setFormData(prev => ({ ...prev, source: val }))}>
                                    <SelectTrigger className="w-full h-14 bg-muted/20 border-2 border-border/50 text-sm font-bold rounded-2xl px-5 text-black">
                                        <SelectValue placeholder="Select source" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-border shadow-2xl">
                                        {SOURCES.map(s => <SelectItem key={s} value={s} className="text-xs font-bold">{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Financial & Timing */}
                    <div className="bg-card shadow-sm rounded-[2.5rem] p-8 border border-border space-y-6">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary border-b border-border/50 pb-4">Logic & Value</h3>
                        
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Expected Value</label>
                            <div className="relative">
                                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground opacity-50" />
                                <input 
                                    type="number"
                                    name="expectedValue"
                                    value={formData.expectedValue}
                                    onChange={handleChange}
                                    className="w-full bg-muted/20 border-2 border-border/50 pl-11 pr-4 py-4 rounded-2xl text-base font-black tabular-nums focus:border-primary outline-none transition-all"
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Closing Window</label>
                            <div className="relative">
                                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground opacity-50" />
                                <input 
                                    type="date"
                                    name="expectedCloseDate"
                                    value={formData.expectedCloseDate}
                                    onChange={handleChange}
                                    className="w-full bg-muted/20 border-2 border-border/50 pl-11 pr-4 py-4 rounded-2xl text-base font-bold focus:border-primary outline-none transition-all"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Entities */}
                    <div className="bg-card shadow-sm rounded-[2.5rem] p-8 border border-border space-y-6">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary border-b border-border/50 pb-4">Relationships</h3>
                        
                        <div className="space-y-1.5 text-black">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Target Organization</label>
                            <Select value={formData.companyId || ""} onValueChange={(val) => setFormData(prev => ({ ...prev, companyId: val }))}>
                                <SelectTrigger className="w-full h-11 bg-muted/20 border-2 border-border/50 text-[11px] font-black uppercase tracking-widest rounded-xl px-4 text-black">
                                    <SelectValue placeholder="Establish direct link..." />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-border shadow-2xl max-h-[300px]">
                                    {workspaceCompanies.map(c => (
                                        <SelectItem key={c._id || c.id} value={c._id || c.id} className="text-xs font-bold text-black">
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1.5 text-black">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Primary Decision Maker</label>
                            <Select value={formData.primaryContactId || ""} onValueChange={(val) => setFormData(prev => ({ ...prev, primaryContactId: val }))}>
                                <SelectTrigger className="w-full h-11 bg-muted/20 border-2 border-border/50 text-[11px] font-black uppercase tracking-widest rounded-xl px-4 text-black">
                                    <SelectValue placeholder="Link key stakeholder..." />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-border shadow-2xl max-h-[300px]">
                                    {workspaceContacts.map(c => (
                                        <SelectItem key={c._id || c.id} value={c._id || c.id} className="text-xs font-bold text-black">
                                            {getContactName(c) || 'Contact Entity'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1.5 text-black">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Dealsmith (Owner)</label>
                            <Select value={formData.ownerId || ""} onValueChange={(val) => setFormData(prev => ({ ...prev, ownerId: val }))}>
                                <SelectTrigger className="w-full h-11 bg-muted/20 border-2 border-border/50 text-[11px] font-black uppercase tracking-widest rounded-xl px-4 text-black">
                                    <SelectValue placeholder="Select internal owner..." />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-border shadow-2xl">
                                    {workspaceMembers.map(m => (
                                        <SelectItem key={m._id || m.id} value={m._id || m.id} className="text-xs font-bold text-black">
                                            {m.name || m.email}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Context */}
                    <div className="md:col-span-2 bg-card shadow-sm rounded-[2.5rem] p-8 border border-border space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1 opacity-70">Technical & Contextual Notes</label>
                        <textarea 
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            placeholder="Add strategic context, blockers, or expansion potential highlights..."
                            rows={4}
                            className="w-full bg-muted/20 border-2 border-border/50 p-6 rounded-3xl text-sm font-bold focus:border-primary outline-none transition-all resize-none leading-relaxed"
                        />
                    </div>

                    {/* Action Bar */}
                    <div className="md:col-span-2 flex items-center justify-end gap-4 pb-12">
                        <Button type="button" variant="ghost" onClick={() => navigate(-1)}
                            className="h-14 px-8 rounded-2xl font-black uppercase tracking-widest text-xs text-muted-foreground hover:bg-muted/50 transition-all border border-transparent hover:border-border">
                            Discard
                        </Button>
                        <Button 
                            type="submit" 
                            disabled={submitting}
                            className="bg-primary text-primary-foreground h-14 px-12 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
                        >
                            {submitting ? 'Broadcasting...' : isEditMode ? 'Commit Changes' : 'Launch Opportunity'}
                        </Button>
                    </div>
                </form>
            </div>
        </PageShell>
    );
}
